import type {
  DeepPartial,
  DefaultValues,
  FieldValues,
  UseFormProps as UseFormPropsBase,
  UseFormReturn as UseFormReturnBase,
} from "react-hook-form";
import { useForm as useReactHookForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import { type StorageDriver, type StorageKey } from "@exonn/storage";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { deepEq } from "@exonn/utils";

type PersistedState<TFieldValues extends FieldValues = FieldValues> = {
  formValues: TFieldValues;
};

export type UseFormProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
> = UseFormPropsBase<TFieldValues, TContext> & {
  onValuesChange?: (values: DeepPartial<TFieldValues>) => void;
  storageKey: StorageKey;
  storage?: StorageDriver;
  debug?: boolean;
};

export type UseFormReturn<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = {
  form: UseFormReturnBase<TFieldValues, TContext, TTransformedValues>;
  clearStorage: () => void;
  getValuesFromStorage: () => TFieldValues | null;
  saveValuesToStorage: (value: TFieldValues) => void;
  disablePersistence: () => void;
  enablePersistence: () => void;
  isPersisted: boolean;
};

export const useForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props: UseFormProps<TFieldValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const {
    onValuesChange,
    storageKey,
    storage = memoryStorageDriver,
    defaultValues,
    ...restProps
  } = props;

  const storageKeyRef = useRef<StorageKey>(storageKey);

  useEffect(() => {
    storageKeyRef.current = storageKey;
  });

  const clearStorage = useCallback(() => {
    storage.remove(storageKeyRef.current);
  }, [storage]);

  const getValuesFromStorage = useCallback(() => {
    const state = storage.get<PersistedState<TFieldValues>>(
      storageKeyRef.current,
    );

    return state?.formValues ?? null;
  }, [storage]);

  const [isPersisted, setIsPersisted] = useState<boolean>(
    !!getValuesFromStorage(),
  );

  const form = useReactHookForm<TFieldValues, TContext, TTransformedValues>({
    ...restProps,
    defaultValues: defaultValues as DefaultValues<TFieldValues>,
  });

  const restoredPersistedValues = useRef(false);
  const disableFormValuesPersistence = useRef(false);

  const disablePersistence = useCallback(() => {
    disableFormValuesPersistence.current = true;
  }, []);

  const enablePersistence = useCallback(() => {
    disableFormValuesPersistence.current = false;
  }, []);

  if (!restoredPersistedValues.current) {
    const persistedValues = getValuesFromStorage();
    if (persistedValues) {
      disablePersistence();

      form.reset(persistedValues, {
        keepDefaultValues: true,
      });
      enablePersistence();
    }
    restoredPersistedValues.current = true;
  }

  const saveValuesToStorage = useCallback(
    (values: TFieldValues) => {
      storage.set<PersistedState<TFieldValues>>(storageKeyRef.current, {
        formValues: values,
      });
    },
    [storage],
  );

  useEffect(() => {
    const subscription = form.watch((values) => {
      onValuesChange?.(values);
      if (disableFormValuesPersistence.current) {
        return;
      }
      // console.log("values", values);
      // console.log("defaultValues", defaultValues);
      if (deepEq(values, defaultValues)) {
        clearStorage();
      } else {
        saveValuesToStorage(values as TFieldValues);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onValuesChange, saveValuesToStorage, defaultValues, clearStorage]);

  useEffect(() => {
    return storage.on<PersistedState>(
      "change",
      storageKeyRef.current,
      (state) => {
        setIsPersisted(!!state);
      },
    );
  }, [storage]);

  return {
    form,
    clearStorage,
    getValuesFromStorage,
    saveValuesToStorage,
    disablePersistence,
    enablePersistence,
    isPersisted,
  };
};
